/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useEffect } from 'react';

var palDataHookStore = {
  // Default skip key. This is used in place of a new store for any skipped queries
  skip: {
    makeRequest: false,
    setMakeRequest: function setMakeRequest() {},
    setAbortController: function setAbortController() {},
    reset: function reset() {},
    abortRequest: function abortRequest() {},
    cleanup: function cleanup() {},
    setPollingInterval: function setPollingInterval() {},
    state: {
      loading: true,
      error: null,
      errors: null,
      data: null,
      status: null,
      aborted: false,
      refetch: function refetch() {},
      updateData: function updateData() {},
      abort: function abort() {},
      updated: 0
    },
    setters: [],
    setState: function setState() {}
  }
};
var createDataStore = function createDataStore(_ref) {
  var key = _ref.key;
  // Append the key to the store
  palDataHookStore[key] = {
    makeRequest: true,
    setMakeRequest: function setMakeRequest(makeRequest) {
      this.makeRequest = makeRequest;
    },
    setAbortController: function setAbortController(controller) {
      this.abortController = controller;
    },
    reset: function reset() {
      // Enable requests
      this.setMakeRequest(new Date());

      // Reset existing state
      this.setState({
        loading: true,
        data: null,
        error: null,
        errors: null,
        status: null,
        aborted: false
      });
    },
    abortRequest: function abortRequest() {
      if (this.abortController && !this.state.aborted) {
        this.abortController.abort();
        this.setState({
          aborted: true
        });
      }
    },
    cleanup: function cleanup() {
      var _this = this;
      var timeout = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 10000;
      if (this.cleanUpTimeOut) {
        clearTimeout(this.cleanUpTimeOut);
      }
      var timeoutId = setTimeout(function () {
        if (_this.setters.length === 0) {
          clearTimeout(_this.pollingInterval);
          delete palDataHookStore[key];
        }
      }, timeout);
      this.cleanUpTimeOut = timeoutId;
    },
    setPollingInterval: function setPollingInterval(interval) {
      this.pollingInterval = interval;
    },
    state: {
      loading: true,
      error: null,
      errors: null,
      data: null,
      status: null,
      aborted: false,
      updated: 0
    },
    setters: [],
    setState: function setState(_ref2) {
      var _this2 = this;
      var _ref2$loading = _ref2.loading,
        loading = _ref2$loading === void 0 ? this.state.loading : _ref2$loading,
        _ref2$data = _ref2.data,
        data = _ref2$data === void 0 ? this.state.data : _ref2$data,
        _ref2$error = _ref2.error,
        error = _ref2$error === void 0 ? this.state.error : _ref2$error,
        _ref2$errors = _ref2.errors,
        errors = _ref2$errors === void 0 ? this.state.errors : _ref2$errors,
        _ref2$status = _ref2.status,
        status = _ref2$status === void 0 ? this.state.status : _ref2$status,
        _ref2$aborted = _ref2.aborted,
        aborted = _ref2$aborted === void 0 ? this.state.aborted : _ref2$aborted,
        _ref2$updated = _ref2.updated,
        updated = _ref2$updated === void 0 ? this.state.updated : _ref2$updated;
      this.state = _objectSpread2(_objectSpread2({}, this.state), {}, {
        loading: loading,
        data: data,
        error: error,
        errors: errors,
        status: status,
        aborted: aborted,
        updated: updated
      });
      this.setters.forEach(function (setter) {
        return setter(_this2.state);
      });
    }
  };
  palDataHookStore[key].setState = palDataHookStore[key].setState.bind(palDataHookStore[key]);
  return palDataHookStore[key];
};
var useDataStore = function useDataStore(_ref3) {
  var key = _ref3.key;
  var store;
  // If the key already exists in the store append the key
  if (!palDataHookStore[key]) {
    store = createDataStore({
      key: key
    });
  } else {
    store = palDataHookStore[key];
  }

  // Establish react hook to follow state of this key
  var _useState = useState(store.state),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];

  // Refreshes state from the cache when an new key is provided
  useEffect(function () {
    setState(store.state);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  // If this datahook isn't being tracked by the store yet add it.
  if (!store.setters.includes(setState)) {
    store.setters.push(setState);
  }

  // If this data hook unmounts remove it from the store's setters
  useEffect(function () {
    return function () {
      store.setters.splice(store.setters.indexOf(setState), 1);
    };
  }, [store.setters]);
  return [{
    listeners: {
      makeRequest: store.makeRequest
    },
    response: _objectSpread2(_objectSpread2({
      key: key
    }, state), {}, {
      refetch: function refetch() {
        store.reset();
      },
      updateData: function updateData(data) {
        // Exposed for optimistic or manual updates of the data response
        store.setState(_objectSpread2(_objectSpread2({}, store.state), {}, {
          data: data
        }));
      },
      abort: function abort() {
        store.abortRequest();
      }
    })
  }, store.setState];
};
var useStore = useDataStore;

export { useStore as default, palDataHookStore };
